import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useMap,
  Polyline,
  ScaleControl,
  TileLayer,
  FeatureGroup,
} from 'react-leaflet';
import { useDispatch, useSelector } from 'react-redux';
import { LatLngBoundsExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { customername, RootState } from '@/index';
import { API_KEY } from '@/config/constants';
import utilMaps from '@/components/utils/utilMaps';
import { push } from 'redux-first-history';
import LayersPanel from './LayersPanel';
import { makeStyles } from '@mui/styles';
import { IconButton } from '@mui/material';
import { LayersOutlined } from '@mui/icons-material';
import CustomZoomControl from './CustomZoomControl';

const useStyles = makeStyles({
  polylineHover: {
    cursor: 'pointer',
  },
  pulsingDot: {
    width: 12,
    height: 12,
    backgroundColor: '#00f',
    borderRadius: '50%',
    animation: '$pulse 1.5s infinite',
    boxShadow: '0 0 0 rgba(0, 0, 255, 0.4)',
  },
  '@keyframes pulse': {
    '0%': {
      transform: 'scale(0.9)',
      boxShadow: '0 0 0 0 rgba(0, 0, 255, 0.4)',
    },
    '70%': {
      transform: 'scale(1)',
      boxShadow: '0 0 0 10px rgba(0, 0, 255, 0)',
    },
    '100%': {
      transform: 'scale(0.9)',
      boxShadow: '0 0 0 0 rgba(0, 0, 255, 0)',
    },
  },
  zoomControl: {
    '& .leaflet-control-zoom': {
      marginTop: 20,
      marginLeft: 10,
    },
  },
});

export const RouteMapContent = ({ controls, routeColors, visibleRouteIds }: any) => {
  const map = useMap();
  const dispatch = useDispatch();
  const { routestrings, depotLocation } = useSelector((state: RootState) => state.routes);
  const [polylines, setPolylines] = useState<any[]>([]);
  const depotMarker = useMemo(
    () => utilMaps.renderDepotMarkerFromLocation(depotLocation),
    [depotLocation]
  );
  const polyRefs = useRef<any[]>([]);
  const classes = useStyles();
  const [showDepots, setShowDepots] = useState(true);
  const [showTouren, setShowTouren] = useState(true);
  const [baseLayer, setBaseLayer] = useState('Standard');
  const [showLayersPanel, setShowLayersPanel] = useState(false);

  const filteredPolylines = useMemo(() => {
    if (!visibleRouteIds || visibleRouteIds.length === 0) return polylines;

    const idSet = new Set(visibleRouteIds);
    return polylines.filter(
      (line) => idSet.has(line.vehicleName) || idSet.has(line.id)
    );
  }, [polylines, visibleRouteIds]);

  const centerMap = useCallback(
    (polys: any[]) => {
      const bounds: LatLngBoundsExpression[] = polys.map(p => p.coords);
      if (bounds.length > 0 && map) {
        map.fitBounds(bounds as any);
      }
    },
    [map]
  );
  useEffect(() => {
    if (!routestrings || Object.keys(routestrings).length === 0) return;
    const flatRoutes = Object.entries(routestrings).flatMap(
      ([vehicleName, vehicleRoutes]) =>
        (Array.isArray(vehicleRoutes) ? vehicleRoutes : []).map(
          ({ id, coords }: any) => ({ id, coords, vehicleName })
        )
    );
    setPolylines(flatRoutes);
    centerMap(flatRoutes);
  }, [routestrings, centerMap]);

  useEffect(() => {
    if (filteredPolylines.length > 0) {
      centerMap(filteredPolylines);
    } else if (polylines.length > 0 && (!visibleRouteIds || visibleRouteIds.length === 0)) {
      centerMap(polylines);
    }
  }, [filteredPolylines, polylines, visibleRouteIds, centerMap]);

  const spotPolyLine = useCallback(
    (ids: string[]) => {
      const linesToHide = polyRefs.current.filter(
        (p, i) => p && polylines.length > 0 && !ids.includes(polylines[i]?.id)
      );
      linesToHide.forEach((line) => {
        line.setStyle({ weight: 0, opacity: 0 });
      });
    },
    [polylines]
  );

  const getTileUrl = () => {
    if (baseLayer === 'LKW') {
      return `https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?style=lite.day&features=vehicle_restrictions:active_and_inactive,congestion_zones:all,environmental_zones:all&apiKey=${API_KEY}&lang=de`;
    }
    if (baseLayer === 'Satellit') {
      return `https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?style=explore.satellite.day&apiKey=${API_KEY}&lang=de`;
    }
    return `https://maps.hereapi.com/v3/base/mc/{z}/{x}/{y}/png8?style=lite.day&apiKey=${API_KEY}&lang=de`;
  };

  return (
    <>
      {controls && controls}
      <IconButton
        onClick={() => setShowLayersPanel(true)}
        sx={{
          position: 'absolute',
          top: 100,
          left: 12,
          zIndex: 1001,
          backgroundColor: '#fff',
          borderRadius: 2,
          padding: '6px',
          boxShadow: 3,
          '&:hover': {
            backgroundColor: '#fff',
          }
        }}
      >
        <LayersOutlined />
      </IconButton>
      {showLayersPanel && (
        <LayersPanel
          showDepots={showDepots}
          showTouren={showTouren}
          selectedBase={baseLayer}
          onToggleDepot={() => setShowDepots(!showDepots)}
          onToggleTouren={() => setShowTouren(!showTouren)}
          onChangeBase={setBaseLayer}
          onClose={() => setShowLayersPanel(false)}
        />
      )}
      <CustomZoomControl />
      {showDepots && <FeatureGroup>{depotMarker}</FeatureGroup>}
      {showTouren && (
        <FeatureGroup>
          {filteredPolylines.map((line, idx) => {
            const coords = line.coords;
            if (!coords || coords.length < 2) return null;
            return (
              <Polyline
                key={`route-${line.id}-${idx}`}
                className={classes.polylineHover}
                positions={coords}
                color={routeColors?.[line.id] || 'blue'}
                pathOptions={{ weight: 3, opacity: 1 }}
                eventHandlers={{
                  click: () => {
                    dispatch(
                      push(
                        '/' +
                        customername +
                        'routes/' +
                        encodeURIComponent(line.vehicleName)
                      )
                    );
                  },
                  mouseover: (e) => {
                    const layer = e.target;
                    layer.setStyle({ weight: 5, opacity: 1 });
                    layer.bringToFront();
                  },
                  mouseout: (e) => {
                    const layer = e.target;
                    layer.setStyle({ weight: 3, opacity: 1 });
                  },
                }}
                ref={(el) => (polyRefs.current[idx] = el)}
              />
            );
          })}
        </FeatureGroup>
      )}
      <TileLayer
        url={getTileUrl()}
        attribution={`Map © ${new Date().getFullYear()} HERE`}
        subdomains="1234"
        maxZoom={20}
        updateWhenIdle={true}
      />
      <ScaleControl />
    </>
  );
};